<template>
  <header id="Header">
    <div class="bg-arc"></div>

    <div class="bar">
      <div class="brand">
        <i class="el-icon-collection-tag icon"></i>
        <span class="brand-text">{{ $t("app.title") }}</span>
      </div>

      <div class="info">
        <span class="contact">
          <i class="el-icon-message"></i>
          maxwellmedtech@163.com
        </span>
        <el-dropdown
          v-if="accountMenuVisible"
          class="account-dropdown"
          @command="handleUserCommand"
          trigger="click"
        >
          <span class="account-button">
            <i class="el-icon-user-solid"></i>
            <span class="account-name">{{ displayName }}</span>
            <i class="el-icon-arrow-down account-arrow"></i>
          </span>
          <el-dropdown-menu slot="dropdown" class="account-dropdown-menu">
            <el-dropdown-item disabled>
              <div class="account-summary">
                <div class="account-summary-label">{{ isEn ? 'Current account' : '当前账号' }}</div>
                <div class="account-summary-name">{{ displayName }}</div>
                <div class="account-summary-role">{{ accountStatusLabel }}</div>
              </div>
            </el-dropdown-item>
            <el-dropdown-item v-if="isLoggedIn && isAdmin" command="process" divided>
              <i class="el-icon-upload2"></i> {{ isEn ? 'Data Processing' : '数据处理' }}
            </el-dropdown-item>
            <el-dropdown-item v-else-if="isLoggedIn" command="data" divided>
              <i class="el-icon-document"></i> {{ isEn ? 'Historical Data' : '历史数据' }}
            </el-dropdown-item>
            <el-dropdown-item v-if="isLoggedIn && isAdmin" command="data">
              <i class="el-icon-document"></i> {{ isEn ? 'Historical Data' : '历史数据' }}
            </el-dropdown-item>
            <el-dropdown-item v-if="isLoggedIn && isAdmin" command="admin">
              <i class="el-icon-setting"></i> {{ isEn ? 'Admin Management' : '后台管理' }}
            </el-dropdown-item>
            <el-dropdown-item v-if="isLoggedIn && isAdmin" command="models">
              <i class="el-icon-upload"></i> {{ isEn ? 'Model Updates' : '模型更新' }}
            </el-dropdown-item>
            <el-dropdown-item command="logout" divided>
              <i class="el-icon-switch-button"></i> {{ $t('user.logout') }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-button
          v-else
          class="login-button"
          size="mini"
          type="primary"
          plain
          @click="goLogin"
        >
          <i class="el-icon-user"></i>
          {{ isEn ? 'Log in' : '登录' }}
        </el-button>

        <el-select
          v-model="lang"
          size="mini"
          class="lang-select"
          @change="changeLang"
          popper-class="lang-popper"
        >
          <el-option :label="isEn ? 'Chinese' : '中文'" value="zh" />
          <el-option label="English" value="en" />
        </el-select>
      </div>
    </div>
  </header>
</template>

<script>
import { getLocale, setLocale } from "@/i18n";

export default {
  name: "Header",
  data() {
    return {
      lang: getLocale(),
      subtitle: "",
      username: null,
      email: null,
      isLoggedIn: false,
      isAdmin: false,
      role: "",
      hasToken: false,
      isLoadingUser: false,
    };
  },
  computed: {
    isEn() {
      return this.$i18n.locale === 'en';
    },
    accountMenuVisible() {
      return this.hasToken || this.isLoggedIn;
    },
    displayName() {
      if (this.isLoadingUser && !this.email && !this.username) {
        return this.isEn ? 'Loading account...' : '账号加载中...';
      }
      return this.email || this.username || (this.isEn ? 'Account' : '账号');
    },
    roleLabel() {
      if (this.isAdmin) {
        return this.isEn ? 'Administrator' : '管理员';
      }
      return this.isEn ? 'User' : '普通用户';
    },
    accountStatusLabel() {
      if (this.isLoadingUser) {
        return this.isEn ? 'Checking login status' : '正在确认登录状态';
      }
      return this.roleLabel;
    },
  },
  mounted() {
    const token = localStorage.getItem('access_token');
    this.hasToken = !!token;
    if (token) {
      this.loadUserInfo();
    }
    this.$root.$on('user-logged-in', () => {
      this.hasToken = !!localStorage.getItem('access_token');
      this.loadUserInfo();
    });
  },
  beforeDestroy() {
    this.$root.$off('user-logged-in');
  },
  methods: {
    changeLang(v) {
      setLocale(v);
    },
    clearLoginState() {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      this.username = null;
      this.email = null;
      this.isLoggedIn = false;
      this.isAdmin = false;
      this.role = "";
      this.hasToken = false;
      this.isLoadingUser = false;
    },
    async loadUserInfo() {
      this.hasToken = !!localStorage.getItem('access_token');
      if (!this.hasToken) {
        this.clearLoginState();
        return;
      }
      this.isLoadingUser = true;
      try {
        const authBaseUrl = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
        const response = await this.$http.get(`${authBaseUrl}/me`);
        const user = response.data || {};
        this.username = user.username || null;
        this.email = user.email || null;
        this.role = user.role || "";
        this.isLoggedIn = !!(user.id || user.user_id || this.username || this.email);
        this.isAdmin = user.role === 'super_admin' || user.is_superuser === true;
      } catch (error) {
        console.error('Failed to load user info:', error);
        this.clearLoginState();
        if (this.$route.path !== '/login') {
          this.$message.warning(this.isEn ? 'Login expired, please log in again' : '登录已过期，请重新登录');
          this.$router.push('/login').catch(() => {});
        }
      } finally {
        this.isLoadingUser = false;
      }
    },
    goLogin() {
      this.$router.push('/login').catch(() => {});
    },
    handleUserCommand(command) {
      switch (command) {
        case 'noop':
          break;
        case 'process':
          this.$router.push('/').catch(() => {});
          break;
        case 'data':
          this.$router.push('/results').catch(() => {});
          break;
        case 'admin':
          this.$router.push('/admin/users').catch(() => {});
          break;
        case 'models':
          this.$router.push('/admin/models').catch(() => {});
          break;
        case 'logout':
          this.$confirm(
            this.isEn ? 'Are you sure you want to log out?' : '确定要退出登录吗？',
            this.isEn ? 'Notice' : '提示',
            {
              confirmButtonText: this.isEn ? 'OK' : '确定',
              cancelButtonText: this.isEn ? 'Cancel' : '取消',
              type: 'warning'
            }
          ).then(() => {
            this.clearLoginState();
            this.$message.success(this.isEn ? 'Logged out' : '已退出登录');
            this.$router.push('/login');
          }).catch(() => {});
          break;
      }
    }
  },
};
</script>

<style scoped>
#Header {
  position: relative;
  width: 100%;
  padding: 14px 40px 18px;
  box-sizing: border-box;
  overflow: hidden;
}

.bg-arc {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      at 20% 25%,
      rgba(10, 132, 255, 0.25),
      rgba(10, 132, 255, 0) 55%
    ),
    radial-gradient(
      at 85% 70%,
      rgba(52, 199, 89, 0.28),
      rgba(52, 199, 89, 0) 60%
    ),
    linear-gradient(180deg, #f1f9ff 0%, #ffffff 60%);
  -webkit-mask: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.9),
    rgba(0, 0, 0, 0)
  );
  mask: linear-gradient(to bottom, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
  pointer-events: none;
  z-index: 0;
}

.bar {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 6px 20px;
  backdrop-filter: saturate(160%) blur(14px);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 18px;
  box-shadow: 0 6px 24px -8px rgba(0, 0, 0, 0.1);
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: #0a84ff;
}

.brand .icon {
  font-size: 22px;
  color: #0a84ff;
}

.info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #46515f;
}

.contact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #5f6f7f;
  white-space: nowrap;
}

.contact i {
  color: #0a84ff;
  font-size: 16px;
}

.lang-select ::v-deep .el-input__inner {
  border-radius: 999px;
  padding: 0 14px;
  height: 32px;
  line-height: 32px;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: background 0.2s, border-color 0.2s;
}

.lang-select ::v-deep .el-input__inner:hover {
  background: #ffffff;
}

.account-dropdown {
  flex: 0 1 auto;
}

.account-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 320px;
  padding: 7px 14px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  color: #0a84ff;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.account-button:hover {
  background: #ffffff;
  border-color: rgba(10, 132, 255, 0.3);
  box-shadow: 0 2px 8px rgba(10, 132, 255, 0.15);
}

.account-button i {
  font-size: 14px;
}

.account-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-arrow {
  color: #8a96a3;
  font-size: 12px !important;
}

.account-summary {
  min-width: 220px;
  padding: 4px 0;
  line-height: 1.45;
  cursor: default;
}

.account-summary-label {
  font-size: 12px;
  color: #8a96a3;
}

.account-summary-name {
  margin-top: 2px;
  color: #1f2d3d;
  font-weight: 600;
  word-break: break-all;
}

.account-summary-role {
  margin-top: 2px;
  font-size: 12px;
  color: #0a84ff;
}

.login-button {
  height: 32px;
  padding: 0 14px;
  border-radius: 999px;
}

@media (max-width: 900px) {
  #Header {
    padding: 12px 18px 18px;
  }
  .bar {
    flex-wrap: wrap;
    gap: 14px;
  }
  .info {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .account-button {
    max-width: min(100%, 280px);
  }
}

@media (prefers-color-scheme: dark) {
  .bg-arc {
    background: radial-gradient(
        at 18% 25%,
        rgba(10, 132, 255, 0.35),
        rgba(10, 132, 255, 0) 55%
      ),
      radial-gradient(
        at 85% 70%,
        rgba(52, 199, 89, 0.35),
        rgba(52, 199, 89, 0) 60%
      ),
      linear-gradient(180deg, #1c2530 0%, #11181f 60%);
  }
  .bar {
    background: rgba(30, 40, 52, 0.55);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 6px 28px -10px rgba(0, 0, 0, 0.6);
  }
  .info {
    color: #b7c3d1;
  }
  .account-button,
  .lang-select ::v-deep .el-input__inner {
    background: rgba(55, 65, 78, 0.55);
    color: #e6f1ff;
    border-color: rgba(255, 255, 255, 0.15);
  }
}
</style>
